import { API_BASE_URL, HAS_API_URL } from '../config/api';

/**
 * @returns {{ message: string, code?: string }}
 */
async function friendlyErrorFromResponse(res) {
  let body = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      body = await res.json();
    } catch {
      /* ignore parse errors */
    }
  }
  const serverMsg = typeof body?.error === 'string' ? body.error : null;
  switch (res.status) {
    case 401:
      return { message: 'Your session could not be verified. Please sign out and sign in again.' };
    case 402:
      if (serverMsg === 'INSUFFICIENT_CREDITS') {
        return {
          message: 'You do not have enough credits. Please buy a credit pack to continue.',
          code: 'INSUFFICIENT_CREDITS',
        };
      }
      return { message: serverMsg || 'Payment required.' };
    case 403:
      if (body?.code === 'PROJECT_LIMIT_REACHED' && typeof body?.error === 'string') {
        return { message: body.error, code: 'PROJECT_LIMIT_REACHED' };
      }
      return { message: serverMsg || "You don't have access to this project." };
    case 404:
      return { message: 'This project could not be found.' };
    case 409:
      return { message: serverMsg || 'This action conflicts with the current project state.' };
    case 400:
      return { message: serverMsg || 'The request was invalid. Please try again.' };
    default:
      return { message: serverMsg || 'Something went wrong. Please try again.' };
  }
}

/**
 * Calls POST /api/generate-design. Server updates Firestore when generation completes.
 * @param {{ regenerate?: boolean, imageQuality?: string, pendingCreate?: boolean, projectDraft?: Record<string, unknown> }} [options] — when true, regenerates designs for completed projects; optional `imageQuality` (`standard`|`high`). For new designs without a Firestore doc yet, pass `pendingCreate: true` and `projectDraft`.
 * @returns {{ ok: true, generatedImageUrl?: string, project?: Record<string, unknown> } | { ok: false, error: string, code?: string }}
 */
export async function requestGenerateDesign(user, projectId, options = {}) {
  const { regenerate = false, imageQuality, pendingCreate = false, projectDraft } = options;
  if (!user) {
    return { ok: false, error: 'You must be signed in to generate a design.' };
  }
  if (!HAS_API_URL) {
    return {
      ok: false,
      error: 'The app is not configured to reach the design server. Please contact support.',
    };
  }
  if (!projectId) {
    return { ok: false, error: 'Missing project id.' };
  }

  try {
    const idToken = await user.getIdToken(true);
    const requestUrl = `${API_BASE_URL}/api/generate-design`;

    const res = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        projectId,
        ...(regenerate ? { regenerate: true } : {}),
        ...(typeof imageQuality === 'string' ? { imageQuality } : {}),
        ...(pendingCreate && projectDraft && typeof projectDraft === 'object'
          ? { pendingCreate: true, projectDraft }
          : {}),
      }),
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const project =
        body?.project && typeof body.project === 'object' ? body.project : undefined;
      return {
        ok: true,
        generatedImageUrl: typeof body?.generatedImageUrl === 'string' ? body.generatedImageUrl : '',
        ...(project ? { project } : {}),
      };
    }

    let hasBody = false;
    try {
      const t = await res.clone().text();
      hasBody = typeof t === 'string' && t.length > 0;
    } catch {
      /* ignore */
    }
    console.error('[generate-design] HTTP error', {
      status: res.status,
      statusText: res.statusText,
      hasBody,
    });
    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, ...(code ? { code } : {}) };
  } catch (err) {
    const name = err?.name;
    const message = err?.message;
    const likelyCors =
      typeof message === 'string' &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    console.error('[generate-design] fetch threw (network/CORS or pre-fetch error)', {
      name,
      message,
      likelyCors,
    });
    if (likelyCors) {
      console.error(
        '[generate-design] Hint: if origin is http://localhost:3000, Cloud Run CLIENT_ORIGIN must include that exact origin (comma-separated).'
      );
    }
    return {
      ok: false,
      error:
        process.env.NODE_ENV === 'development' && message
          ? `Could not reach the server. (${message})`
          : 'Could not reach the server. Check your connection and try again.',
    };
  }
}
